import { useCallback, useEffect, useState } from 'react'

import { clearAllEventName } from 'utils/customEvents'

/**
 * Custom React hook to synchronize a state value with the URL query parameter.
 * @param {string} key - The key of the query parameter.
 * @param {string} [defaultValue=''] - The default value if the query parameter is not present.
 * @param {function} [customAction=(decodedValue) => decodedValue] - A custom function to transform the decoded value from the URL.
 *   This function receives the decoded value as an argument and should return either:
 *     - falsy value.
 *     - An array containing two elements:
 *       - The first element is the transformed value to be used as the state (actual value).
 *       - The second element is the expected value in the URL (used for validation).
 *
 * @returns {[string, function]} An array containing the current state value and a function to update it.
 *
 * @example
 * // Basic usage
 *
 * // Using customAction for validation and transformation
 * const [userId, setUserId] = useURLState('user', '', false, (value) => {
 *   const validId = validateUserId(value); // Assume this function exists
 *   return [validId, value]; // Return transformed value and expected value
 * });
 */

const clearUrl = () => {
  const url = new URL(window.location)
  window.history.replaceState({}, '', url.origin + url.pathname)
}

function useURLState(key, defaultValue = '', customAction = () => {}) {
  /**
   * Function to update the URL query parameter based on the provided value.
   * @param {string} newValue - The new value to be set for the query parameter.
   */
  const updateURL = useCallback(
    (newValue) => {
      const url = new URL(window.location)
      if (newValue) {
        url.searchParams.set(key, encodeURIComponent(newValue))
      } else {
        url.searchParams.delete(key)
      }
      window.history.replaceState({}, '', url)
    },
    [key]
  )

  /**
   * State hook to manage the value synchronized with the URL query parameter.
   */
  const [value, setValue] = useState(() => {
    const params = new URLSearchParams(window.location.search)
    const decodedValue = decodeURIComponent(params.get(key) || defaultValue)
    const res = customAction(decodedValue)
    if (res) {
      const actual = res[0] || ''
      const expected = res[1]
      if (expected !== actual) {
        updateURL(actual)
      }
      return actual
    }
    return decodedValue
  })

  /**
   * Effect hook to update the state value when the URL changes.
   */
  useEffect(() => {
    const handlePopState = () => {
      const params = new URLSearchParams(window.location.search)
      setValue(decodeURIComponent(params.get(key)) || defaultValue)
    }
    window.addEventListener(clearAllEventName, clearUrl)
    window.addEventListener('popstate', handlePopState)
    return () => {
      window.removeEventListener('popstate', handlePopState)
      window.removeEventListener(clearAllEventName, clearUrl)
    }
  }, [key, defaultValue])

  /**
   * Effect hook to update the URL when the state value changes.
   */
  useEffect(() => {
    if (value !== defaultValue) {
      updateURL(value)
    }
  }, [value, defaultValue, updateURL])

  /**
   * Return the current value and the function to update it.
   */
  return [value, setValue]
}

export default useURLState
